import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, History, ScanLine } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Button, Checkbox, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { RadiologyAcknowledgeDialog } from './RadiologyAcknowledgeDialog';

interface Addendum {
  id: string;
  sequence: number;
  findings: string;
  impression: string | null;
  recommendation: string | null;
  authoredBy: string | null;
  authorName: string | null;
  authoredAt: string;
}

interface StudyDetail {
  id: string;
  number: string;
  status: string;
  clinicalNotes: string | null;
  studyType: { name: string };
  patient: { fullName: string; mrn: string };
  reportFindings: string | null;
  reportImpression: string | null;
  reportRecommendation: string | null;
  reportedAt: string | null;
  verifiedAt: string | null;
  radiologistName: string | null;
  verifierName: string | null;
  criticalFinding: boolean;
  criticalFindingNote: string | null;
  criticalAckAt: string | null;
  criticalAckTo: string | null;
  criticalAckNote: string | null;
  priors: { id: string; number: string; orderedAt: string; reportImpression: string | null }[];
  addenda: Addendum[];
}

type Mode = 'view' | 'new' | 'edit' | 'addendum';

const MIN_FINDINGS = 5;

function modeFor(detail: StudyDetail, canWrite: boolean): Mode {
  if (!canWrite) return 'view';
  const locked = Boolean(detail.verifiedAt) || ['verified', 'released'].includes(detail.status) || detail.addenda.length > 0;
  if (locked) return 'addendum';
  return detail.reportFindings ? 'edit' : 'new';
}

function ReportText({ label, text }: { label: string; text: string | null }) {
  if (!text) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="whitespace-pre-wrap text-sm">{text}</p>
    </div>
  );
}

function ReportCard({ title, meta, children, tone }: { title: string; meta?: string; children: ReactNode; tone?: 'addendum' }) {
  return (
    <div className={tone === 'addendum' ? 'space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3' : 'space-y-2 rounded-md border p-3'}>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
      {children}
    </div>
  );
}

export function RadiologyReportPage() {
  const { studyId = '' } = useParams<{ studyId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/radiology');
  const { can } = useAuth();
  const canWrite = can('radiology.report.write');
  const canAcknowledge = can('radiology.critical.acknowledge', 'radiology.report.verify');

  const detail = useQuery({
    queryKey: ['radiology-study', studyId],
    queryFn: () => get<StudyDetail>(`/radiology/studies/${studyId}`),
    enabled: Boolean(studyId),
    gcTime: 0,
  });

  if (!detail.data) {
    return (
      <FormScreen title={tr('radiology', 'radiology')} icon={ScanLine} backTo={returnTo}>
        <RecordState error={detail.error} missing={!detail.isLoading} label={tr('radiology', 'loading_the_study')} />
      </FormScreen>
    );
  }

  return <ReportForm key={`${detail.data.id}:${detail.data.addenda.length}`} study={detail.data} canWrite={canWrite} canAcknowledge={canAcknowledge} returnTo={returnTo} />;
}

function ReportForm({ study, canWrite, canAcknowledge, returnTo }: { study: StudyDetail; canWrite: boolean; canAcknowledge: boolean; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mode = modeFor(study, canWrite);
  const editing = mode === 'new' || mode === 'edit';
  const [initial] = useState(() => ({
    findings: editing ? (study.reportFindings ?? '') : '',
    impression: editing ? (study.reportImpression ?? '') : '',
    recommendation: editing ? (study.reportRecommendation ?? '') : '',
  }));
  const [findings, setFindings] = useState(initial.findings);
  const [impression, setImpression] = useState(initial.impression);
  const [recommendation, setRecommendation] = useState(initial.recommendation);
  const [critical, setCritical] = useState(false);
  const [criticalNote, setCriticalNote] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);

  const isAddendum = mode === 'addendum';
  const criticalReady = !critical || criticalNote.trim().length > 0;
  const canSave = mode !== 'view' && findings.trim().length >= MIN_FINDINGS && criticalReady;

  const save = useMutation({
    mutationFn: () =>
      post(`/radiology/studies/${study.id}/report`, {
        findings: findings.trim(),
        impression: impression.trim() || undefined,
        recommendation: recommendation.trim() || undefined,
        isAddendum,
        criticalFinding: critical || undefined,
        criticalFindingNote: critical ? criticalNote.trim() : undefined,
      }),
    onSuccess: () => {
      if (isAddendum) {
        toast.success(tr('radiology', 'addendum_filed'), tr('radiology', 'it_sits_under_the_original_report_with_your'));
      } else {
        toast.success(tr('radiology', 'report_saved'), tr('radiology', 'a_consultant_must_verify_it_before_release'));
      }
      if (critical) toast.warning(tr('radiology', 'critical_finding_flagged'), tr('radiology', 'it_stays_flagged_until_someone_records_who_was'));
      void queryClient.invalidateQueries({ queryKey: ['radiology-study', study.id] });
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error(isAddendum ? tr('radiology', 'could_not_file_the_addendum') : tr('radiology', 'could_not_save_the_report'), describeError(error)),
  });

  const nextAddendum = study.addenda.length + 1;
  const stateWord = study.status === 'released' ? 'released' : 'verified';
  const dirty =
    !save.isSuccess &&
    (findings !== initial.findings || impression !== initial.impression || recommendation !== initial.recommendation || critical || criticalNote !== '');

  return (
    <FormScreen
      title={study.studyType.name}
      description={`${study.patient.fullName} · ${study.patient.mrn} · ${study.number}`}
      eyebrow={tr('radiology', 'radiology')}
      icon={ScanLine}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      {study.criticalFinding &&
        (study.criticalAckAt ? (
          <Alert variant="success" title={`Acknowledged - ${study.criticalAckTo ?? 'the referring doctor'} was told`}>
            <p className="text-xs">
              {dateTime(study.criticalAckAt)}
              {study.criticalAckNote ? ` · Read-back: ${study.criticalAckNote}` : ''}
            </p>
          </Alert>
        ) : (
          <Alert variant="destructive" title={tr('radiology', 'critical_finding_awaiting_acknowledgement')}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p>{study.criticalFindingNote ?? tr('radiology', 'this_study_carries_a_critical_finding')}</p>
              {canAcknowledge && (
                <Button type="button" size="sm" variant="destructive" onClick={() => setAcknowledging(true)}>
                  {tr('radiology', 'acknowledge')}
                </Button>
              )}
            </div>
          </Alert>
        ))}

      {study.clinicalNotes && (
        <Alert>
          <span className="font-medium">{tr('radiology', 'clinical_history')} </span>
          {study.clinicalNotes}
        </Alert>
      )}

      {(study.priors.length > 0 || (study.reportFindings && (mode === 'view' || mode === 'addendum')) || study.addenda.length > 0) && (
        <FormSection title={tr('radiology', 'report_on_record')} icon={History} columns={1}>
          {study.priors.length > 0 && (
            <div className="rounded-md border p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {tr('radiology', 'prior')} {study.studyType.name} {tr('radiology', 'studies')}
              </p>
              <ul className="space-y-1 text-sm">
                {study.priors.map((prior) => (
                  <li key={prior.id}>
                    <span className="text-muted-foreground">{dateTime(prior.orderedAt)}: </span>
                    {prior.reportImpression ?? tr('radiology', 'no_impression_recorded')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {study.reportFindings && (mode === 'view' || mode === 'addendum') && (
            <ReportCard
              title={`Reported by ${study.radiologistName ?? 'an unrecorded user'}`}
              meta={[study.reportedAt ? dateTime(study.reportedAt) : null, study.verifierName ? `Verified by ${study.verifierName}` : null].filter(Boolean).join(' · ')}
            >
              <ReportText label={tr('radiology', 'findings')} text={study.reportFindings} />
              <ReportText label={tr('radiology', 'impression')} text={study.reportImpression} />
              <ReportText label={tr('radiology', 'recommendation')} text={study.reportRecommendation} />
            </ReportCard>
          )}

          {study.addenda.map((addendum) => (
            <ReportCard key={addendum.id} tone="addendum" title={`Addendum ${addendum.sequence} - ${addendum.authorName ?? 'an unrecorded user'} - ${dateTime(addendum.authoredAt)}`}>
              <ReportText label={tr('radiology', 'findings')} text={addendum.findings} />
              <ReportText label={tr('radiology', 'impression')} text={addendum.impression} />
              <ReportText label={tr('radiology', 'recommendation')} text={addendum.recommendation} />
            </ReportCard>
          ))}
        </FormSection>
      )}

      {mode === 'view' && !study.reportFindings && (
        <p className="text-sm text-muted-foreground">{tr('radiology', 'no_report_has_been_written_for_this_study_yet')}</p>
      )}

      {isAddendum && (
        <Alert variant="warning">
          {tr('radiology', 'this_report_has_already_been')} {stateWord}
          {tr('radiology', 'so_the_original_is_locked_your_text_is_filed_as')} {nextAddendum} {tr('radiology', 'under_your_name_and_the_current_time_the')}
        </Alert>
      )}

      {mode !== 'view' && (
        <FormSection title={isAddendum ? tr('radiology', 'addendum_findings') : tr('radiology', 'findings')} icon={FileText} columns={1}>
          <Field label={isAddendum ? tr('radiology', 'addendum_findings') : tr('radiology', 'findings')} required>
            <Textarea rows={isAddendum ? 5 : 8} value={findings} onChange={(e) => setFindings(e.target.value)} />
          </Field>
          <Field label={tr('radiology', 'impression')}>
            <Textarea rows={3} value={impression} onChange={(e) => setImpression(e.target.value)} />
          </Field>
          <Field label={tr('radiology', 'recommendation')}>
            <Textarea rows={2} value={recommendation} onChange={(e) => setRecommendation(e.target.value)} />
          </Field>

          <div className="rounded-md border p-3">
            <Checkbox
              checked={critical}
              onCheckedChange={setCritical}
              label={<span className="font-medium">{tr('radiology', 'critical_finding_notify_the_referring_doctor_now')}</span>}
              description={study.criticalFinding ? tr('radiology', 'this_study_is_already_flagged_tick_it_again') : tr('radiology', 'raises_the_alert_and_keeps_the_study_flagged')}
            />
            {critical && (
              <Field label={tr('radiology', 'critical_finding_note')} required hint={tr('radiology', 'what_the_referring_doctor_must_be_told_up_to')} className="mt-3">
                <Textarea rows={2} maxLength={500} value={criticalNote} onChange={(e) => setCriticalNote(e.target.value)} />
              </Field>
            )}
          </div>
        </FormSection>
      )}

      {mode !== 'view' && (
        <FormActions
          submitLabel={isAddendum ? tr('radiology', 'file_addendum') : tr('radiology', 'save_report')}
          loading={save.isPending}
          incomplete={!canSave}
          cancelTo={returnTo}
        />
      )}

      {acknowledging && <RadiologyAcknowledgeDialog study={study} onClose={() => setAcknowledging(false)} />}
    </FormScreen>
  );
}
